import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, Switch, Popconfirm, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, BellOutlined, CarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import CarsModal from '../components/CarsModal';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'client' | 'valet' | 'admin';
  isActive: boolean;
  createdAt: string;
  buildings?: { id: string; name: string }[];
  cars?: { id: string; licensePlate: string; make: string; model: string; color: string }[];
}

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationUser, setNotificationUser] = useState<User | null>(null);
  const [notificationForm] = Form.useForm();
  const [form] = Form.useForm();
  const [isCarsModalOpen, setIsCarsModalOpen] = useState(false);
  const [carsUser, setCarsUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    queueMicrotask(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    });
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['users', pagination, debouncedSearch],
    queryFn: async () => {
      const offset = (pagination.current - 1) * pagination.pageSize;
      const params = new URLSearchParams();
      params.append('limit', String(pagination.pageSize));
      params.append('offset', String(offset));
      if (debouncedSearch) params.append('search', debouncedSearch);
      const response = await api.get(`/admin/users?${params}`);
      return response.data;
    }
  });

  const { data: buildingsData } = useQuery({
    queryKey: ['buildings'],
    queryFn: async () => {
      const response = await api.get('/admin/buildings');
      return response.data;
    }
  });

  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/admin/settings');
      return response.data;
    }
  });

  const securitySettings = settingsData?.security || {};
  const minPasswordLength = parseInt(securitySettings.minPasswordLength) || 6;
  const passwordRequirements = [
    t('users.passwordMinLength', { count: minPasswordLength }),
  ];
  if (securitySettings.requireUppercase === 'true') passwordRequirements.push(t('users.passwordUppercase'));
  if (securitySettings.requireDigits === 'true') passwordRequirements.push(t('users.passwordDigit'));
  if (securitySettings.requireSpecialChars === 'true') passwordRequirements.push(t('users.passwordSpecial'));

  const createMutation = useMutation({
    mutationFn: (values: Partial<User>) => api.post('/admin/users', values),
    onSuccess: () => {
      message.success(t('users.created'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => message.error(t('users.createError')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<User> }) => api.put(`/admin/users/${id}`, values),
    onSuccess: () => {
      message.success(t('users.updated'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      setEditingUser(null);
      form.resetFields();
    },
    onError: () => message.error(t('users.updateError')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/users/${id}`),
    onSuccess: () => {
      message.success(t('users.deleted'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => message.error(t('users.deleteError')),
  });

  const sendNotificationMutation = useMutation({
    mutationFn: ({ userId, type }: { userId: string; type: string }) => 
      api.post(`/admin/users/${userId}/test-notification`, { type }),
    onSuccess: () => {
      message.success(t('users.notificationSent'));
      setIsNotificationModalOpen(false);
      setNotificationUser(null);
      notificationForm.resetFields();
    },
    onError: () => message.error(t('users.notificationError')),
  });
  const handleSendNotification = (user: User) => {
    setNotificationUser(user);
    setIsNotificationModalOpen(true);
  };

  const handleNotificationOk = () => {
    notificationForm.validateFields().then((values) => {
      if (notificationUser) {
        sendNotificationMutation.mutate({ userId: notificationUser.id, type: values.type });
      }
    });
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      if (editingUser) {
        updateMutation.mutate({ id: editingUser.id, values });
      } else {
        createMutation.mutate(values);
      }
    });
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    const buildingIds = user.buildings?.map(b => b.id) || [];
    form.setFieldsValue({ ...user, buildingIds });
    setIsModalOpen(true);
  };

  const handleManageCars = (user: User) => {
    setCarsUser(user);
    setIsCarsModalOpen(true);
  };

  const columns = [
    { title: t('users.firstName'), dataIndex: 'firstName', key: 'firstName' },
    { title: t('users.lastName'), dataIndex: 'lastName', key: 'lastName' },
    { title: t('users.email'), dataIndex: 'email', key: 'email' },
    {
      title: t('users.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const colors: Record<string, string> = { client: 'blue', valet: 'green', admin: 'red' };
        const roleLabels: Record<string, string> = { client: t('users.client'), valet: t('users.valet'), admin: t('users.admin') };
        return <Tag color={colors[role]}>{roleLabels[role] || role}</Tag>;
      },
    },
    {
      title: t('users.buildings'),
      key: 'buildings',
      render: (_: any, record: User) => (
        <span>{record.buildings?.map(b => b.name).join(', ') || '-'}</span>
      ),
    },
    {
      title: t('users.activeStatus'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: User) => (
        <Switch checked={isActive} onChange={(checked) => updateMutation.mutate({ id: record.id, values: { isActive: checked } })} />
      ),
    },
    {
      title: t('users.actions'),
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Button icon={<BellOutlined />} size="small" onClick={() => handleSendNotification(record)} title={t('users.sendTestNotification')} />
          <Button icon={<CarOutlined />} size="small" onClick={() => handleManageCars(record)} title={t('users.manageCars')} />
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} title={t('common.edit')} />
          <Popconfirm title={t('users.confirmDelete')} onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>{t('users.title')}</h1>
        <Space>
          <Input
            placeholder={t('common.search')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingUser(null); form.resetFields(); setIsModalOpen(true); }}>
            {t('users.addUser')}
          </Button>
        </Space>
      </div>
      <Table 
        dataSource={data?.users || []} 
        columns={columns} 
        rowKey="id" 
        loading={isLoading}
        locale={{
          emptyText: t('common.noData'),
        }}
        pagination={{
          ...pagination,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total: number) => `${t('common.total')}: ${total}`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
        }}
      />
      <Modal title={editingUser ? t('users.editUser') : t('users.addUser')} open={isModalOpen} onOk={handleOk} onCancel={() => { setIsModalOpen(false); setEditingUser(null); }}>
        <Form form={form} layout="vertical">
          <Form.Item name="firstName" label={t('users.firstName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label={t('users.lastName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label={t('users.email')} rules={[{ required: true, type: 'email' }]}>
            <Input disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="phone" label={t('users.phone')}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label={t('users.role')} rules={[{ required: true }]}>
            <Select>
              <Select.Option value="client">{t('users.client')}</Select.Option>
              <Select.Option value="valet">{t('users.valet')}</Select.Option>
              <Select.Option value="admin">{t('users.admin')}</Select.Option>
            </Select>
          </Form.Item>
          {editingUser && (
            <Form.Item name="buildingIds" label={t('users.buildings')}>
              <Select mode="multiple" placeholder={t('users.selectBuildings')}>
                {buildingsData?.buildings?.map((b: any) => (
                  <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {editingUser ? (
            <Form.Item name="password" label={t('users.newPassword')} help={`${t('users.passwordRequirements')}: ${passwordRequirements.join(', ')}`}>
              <Input.Password placeholder={t('users.leaveBlank')} />
            </Form.Item>
          ) : (
            <Form.Item name="password" label={t('users.password')} rules={[{ required: true }]} help={`${t('users.passwordRequirements')}: ${passwordRequirements.join(', ')}`}>
              <Input.Password />
            </Form.Item>
          )}
        </Form>
      </Modal>
      <Modal 
        title={t('users.sendTestNotification')} 
        open={isNotificationModalOpen} 
        onOk={handleNotificationOk} 
        onCancel={() => { setIsNotificationModalOpen(false); setNotificationUser(null); notificationForm.resetFields(); }}
        confirmLoading={sendNotificationMutation.isPending}
      >
        <p>{t('users.user')}: <strong>{notificationUser?.firstName} {notificationUser?.lastName}</strong></p>
        <Form form={notificationForm} layout="vertical">
          <Form.Item name="type" label={t('users.notificationType')} rules={[{ required: true, message: t('users.selectType') }]}>
            <Select placeholder={t('users.selectType')}>
              <Select.Option value="accepted">{t('notifications.accepted')}</Select.Option>
              <Select.Option value="delivered">{t('notifications.delivered')}</Select.Option>
              <Select.Option value="completed">{t('notifications.completed')}</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <CarsModal
        open={isCarsModalOpen}
        onClose={() => { setIsCarsModalOpen(false); setCarsUser(null); }}
        userId={carsUser?.id || ''}
        userName={carsUser ? `${carsUser.firstName} ${carsUser.lastName}` : ''}
      />
    </div>
  );
};

export default UsersPage;
